import axios from 'axios'
import { Message } from 'element-ui'
import router from '@/router'

const request = axios.create({
  baseURL: '/api',
  timeout: 120000  // 默认2分钟，复杂操作（备份恢复等）单独设置更长超时
})

// Token 刷新锁，防止并发请求时多次刷新
let isRefreshing = false
let refreshSubscribers = []

function onRefreshed(token) {
  refreshSubscribers.forEach(cb => cb(token))
  refreshSubscribers = []
}

function addRefreshSubscriber(cb) {
  refreshSubscribers.push(cb)
}

// 请求拦截器
request.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`
    }
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// 响应拦截器
request.interceptors.response.use(
  response => {
    // blob 类型（导出文件）不处理 code
    if (response.config.responseType === 'blob') {
      return response
    }
    const res = response.data
    if (res.code === 401) {
      return handleTokenExpired(response.config)
    }
    if (res.code !== 200) {
      Message.error(res.msg || '请求失败')
      return Promise.reject(new Error(res.msg || '请求失败'))
    }
    return res
  },
  error => {
    if (error.response && error.response.status === 401) {
      return handleTokenExpired(error.config)
    }
    // blob 类型的错误不弹提示
    if (error.config?.responseType === 'blob') {
      return Promise.reject(error)
    }
    const backendMsg = error.response?.data?.msg
    if (backendMsg) {
      Message.error(backendMsg)
    } else {
      Message.error(error.message || '网络错误')
    }
    return Promise.reject(error)
  }
)

// JWT Token 自动刷新
async function handleTokenExpired(failedConfig) {
  const refreshToken = localStorage.getItem('refresh_token')
  if (!refreshToken) {
    redirectToLogin()
    return Promise.reject(new Error('未登录'))
  }

  // 防止重试后的请求再次触发刷新（无限循环保护）
  if (failedConfig._retry) {
    redirectToLogin()
    return Promise.reject(new Error('Token 刷新失败'))
  }
  failedConfig._retry = true

  if (!isRefreshing) {
    isRefreshing = true
    try {
      const res = await axios.post('/api/auth/refresh', {}, {
        headers: { 'Authorization': `Bearer ${refreshToken}` }
      })
      if (res.data.code === 200 && res.data.data) {
        const { token, user } = res.data.data
        localStorage.setItem('token', token)
        if (user) {
          localStorage.setItem('user', JSON.stringify(user))
        }
        onRefreshed(token)
        // 重试原请求
        failedConfig.headers['Authorization'] = `Bearer ${token}`
        return request(failedConfig)
      } else {
        redirectToLogin()
        return Promise.reject(new Error('刷新失败'))
      }
    } catch (e) {
      redirectToLogin()
      return Promise.reject(e)
    } finally {
      isRefreshing = false
    }
  } else {
    // 正在刷新中，等待刷新完成后重试
    return new Promise(resolve => {
      addRefreshSubscriber(token => {
        failedConfig.headers['Authorization'] = `Bearer ${token}`
        resolve(request(failedConfig))
      })
    })
  }
}

function redirectToLogin() {
  Message.error('登录已过期，请重新登录')
  localStorage.removeItem('token')
  localStorage.removeItem('refresh_token')
  localStorage.removeItem('user')
  router.push('/login')
}

export default request
